import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { Button, Icon } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { color, font } from 'src/shared/styles/variables';

import { useVideoSessionPageQuery } from '../documents/videoSessionPageQuery.generated';

const StyledPageWrapper = styled.div({
    background: 'linear-gradient(180deg, #2E3B6C 41.88%, #0A1745 100%)',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    color: color.white,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledLoadingMessage = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 3,
    alignItems: 'center',
    justifyContent: 'center',
});
const StyledLoadingSpinner = styled(Icon)(
    {
        margin: '6rem',
    },
    font.black(font.size.headerExtended),
);
const StyledHeader = styled.p({ paddingBottom: '0.25rem' }, font.black(font.size.extraLarge));
const StyledMessage = styled.p({}, font.light(font.size.medium));

const StyledVideoLinkWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
});

const fadeIn = keyframes`{
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
     }
}`;
const VideoText = styled.p(
    { color: color.white, animation: `${fadeIn} 0.1s ease` },
    font.light(font.size.medium),
);
const VideoLink = styled(Button)(
    {
        color: color.white,
        animation: `${fadeIn} 0.1s ease`,
        userSelect: 'auto',
    },
    font.medium(font.size.medium),
);

const TIME_UNTIL_ZOOM_REDIRECT = 2000; // 2 secs
const TIME_UNTIL_RATINGS_REDIRECT = 500; // 0.5 secs

interface Props {
    sessionUuid: string;
}
export const JoinVideoSessionPage: React.FC<Props> = ({ sessionUuid }) => {
    const { t } = useTranslation();
    const { data, loading, called, error } = useVideoSessionPageQuery({
        client: appClient,
        variables: { uuid: sessionUuid },
    });

    const [openedZoom, setOpenedZoom] = useState(false);

    // Automatically open Zoom window.
    useEffect(() => {
        let zoomDelayedRedirect: number;
        if (data && data.coachingVisit && data.coachingVisit.zoomUrl) {
            zoomDelayedRedirect = window.setTimeout(() => {
                if (data && data.coachingVisit && data.coachingVisit.zoomUrl && !openedZoom) {
                    const newWindow = window.open(data.coachingVisit.zoomUrl);
                    setOpenedZoom(!!newWindow);
                }
            }, TIME_UNTIL_ZOOM_REDIRECT);
        }
        return () => clearTimeout(zoomDelayedRedirect);
    }, [sessionUuid, data]);

    // Redirect to Ratings after Zoom has been open for a little bit.
    useEffect(() => {
        let ratingsRedirect: number;
        if (openedZoom) {
            ratingsRedirect = window.setTimeout(() => {
                navigationService.navigate('/rate-session', { session: sessionUuid });
            }, TIME_UNTIL_RATINGS_REDIRECT);
        }
        return () => clearTimeout(ratingsRedirect);
    }, [openedZoom]);

    const handleError = (message: string = config.messages.genericError) => {
        toastNotificationsService.renderErrorToast(message);
        navigationService.navigate('/');

        return null;
    };

    const noDataReceived = called && !loading && !data;

    if (error || noDataReceived) {
        return handleError(error && error.message);
    }

    return (
        <StyledPageWrapper>
            <StyledLoadingMessage>
                <div>
                    <StyledLoadingSpinner
                        type="loading"
                        spin
                        style={{ opacity: openedZoom ? 0 : 100 }}
                    />
                </div>
                <StyledHeader>{t('videoSessions:join_video.title')}</StyledHeader>
                <StyledMessage>{t('videoSessions:join_video.description')}</StyledMessage>
            </StyledLoadingMessage>
            <StyledVideoLinkWrapper>
                {called && !loading && data && data.coachingVisit && data.coachingVisit.zoomUrl ? (
                    <React.Fragment>
                        <VideoText>{t('videoSessions:join_video.not_load')}</VideoText>
                        <VideoLink
                            href={data.coachingVisit.zoomUrl}
                            onClick={() => setOpenedZoom(true)}
                            type="link"
                            target="_blank"
                        >
                            {data.coachingVisit.zoomUrl}
                        </VideoLink>
                    </React.Fragment>
                ) : null}
            </StyledVideoLinkWrapper>
        </StyledPageWrapper>
    );
};
